import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { House, Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { firstRoute } from "../lib/perms";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

export default function Login() {
    const { login, register, refresh, formatApiErrorDetail } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "login") {
                const u = await login(email, password);
                navigate(firstRoute(u));
            } else {
                const u = await register(name, email, password);
                navigate(firstRoute(u));
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        // Top-level: normal full-page redirect. Embedded in iframe: use a popup
        // (Google blocks its login screen inside iframes via X-Frame-Options).
        if (window.self === window.top) {
            window.location.href = authUrl;
            return;
        }
        const w = 500, h = 650;
        const popup = window.open(
            authUrl,
            "care_oauth",
            `width=${w},height=${h},left=${(screen.width - w) / 2},top=${(screen.height - h) / 2}`
        );
        if (!popup) {
            window.parent.postMessage({ type: "care:openPopup", url: authUrl }, "*");
        }
    };

    // Receive the session token posted back by the OAuth popup.
    useEffect(() => {
        const onMessage = (ev) => {
            if (ev.origin !== window.location.origin) return;
            if (ev.data?.type === "care:authResult" && ev.data.ok && ev.data.token) {
                localStorage.setItem("auth_token", ev.data.token);
                refresh().then((u) => navigate(u ? firstRoute(u) : "/"));
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [refresh, navigate]);

    return (
        <div className="min-h-screen md:grid md:grid-cols-2">
            {/* Left brand panel */}
            <div className="relative hidden overflow-hidden bg-primary md:block">
                <img
                    src="https://images.pexels.com/photos/29012619/pexels-photo-29012619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Casa"
                    className="absolute inset-0 h-full w-full object-cover opacity-25"
                />
                <div className="relative flex h-full flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                            <House className="h-5 w-5" />
                        </div>
                        <span className="font-heading text-xl font-bold">Casa Financeiro</span>
                    </div>
                    <div>
                        <h2 className="font-heading text-4xl font-light leading-tight">Controle total das finanças e da equipe do seu lar.</h2>
                        <p className="mt-4 max-w-md text-white/70">Despesas, saldo, cartão de ponto e relatórios diários — com preenchimento por voz e IA para você não precisar digitar.</p>
                    </div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/50">Gestão doméstica inteligente</p>
                </div>
            </div>

            {/* Right form */}
            <div className="flex min-h-screen items-center justify-center bg-background p-6">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex items-center gap-3 md:hidden">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                            <House className="h-5 w-5" />
                        </div>
                        <span className="font-heading text-lg font-bold text-primary">Casa Financeiro</span>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{mode === "login" ? "Bem-vindo de volta" : "Criar conta de gestor"}</p>
                    <h1 className="mt-2 font-heading text-3xl font-light tracking-tight">{mode === "login" ? "Entrar na sua conta" : "Comece agora"}</h1>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
                        {mode === "register" && (
                            <div>
                                <Label>Nome completo</Label>
                                <div className="relative mt-1">
                                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl pl-9" placeholder="Seu nome" />
                                </div>
                            </div>
                        )}
                        <div>
                            <Label>E-mail</Label>
                            <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl pl-9" placeholder="voce@email.com" />
                            </div>
                        </div>
                        <div>
                            <Label>Senha</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl pl-9" placeholder="••••••••" />
                            </div>
                        </div>
                        <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full rounded-full py-6 text-base">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {mode === "login" ? "Entrar" : "Criar conta"}
                        </Button>
                    </form>

                    <div className="my-6 flex items-center gap-4">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs uppercase tracking-widest text-muted-foreground">ou</span>
                        <div className="h-px flex-1 bg-border" />
                    </div>

                    <button onClick={googleLogin} data-testid="google-login-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-border bg-white py-3.5 text-sm font-medium transition-colors hover:bg-muted">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
                        Continuar com Google
                    </button>

                    <p className="mt-8 text-center text-sm text-muted-foreground">
                        {mode === "login" ? "Não tem conta de gestor?" : "Já tem conta?"}{" "}
                        <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode" className="font-semibold text-primary underline-offset-2 hover:underline">
                            {mode === "login" ? "Cadastre-se" : "Entrar"}
                        </button>
                    </p>
                    <p className="mt-2 text-center text-xs text-muted-foreground">Colaboradores recebem acesso do gestor.</p>
                </div>
            </div>
        </div>
    );
}
